import React, { useCallback, useEffect, useState } from 'react';
import LicenseBadge from './LicenseBadge';
import ConfirmDialog from './ConfirmDialog';
import { lifecycleStatus, heartbeatStatus, WARN_DAYS, GRACE_DAYS } from './licenseStatus';

/**
 * <HeartbeatMonitorPanel> - mandantenuebergreifende Ueberwachung (Ueberwachen).
 *
 * Aggregiert im Frontend ueber vorhandene Endpunkte (kein Backend-Read ergaenzt):
 *   GET /admin/tenants                        -> Mandantenliste
 *   GET /admin/tenants/{id}/onprem-licenses   -> Deployments je Mandant
 *   GET /admin/heartbeat-alerts               -> offene Heartbeat-Alarme (global)
 *   POST /admin/heartbeat-alerts/{id}/ack     -> Alarm quittieren
 *
 * Props: api (useApi().api), showToast, t.
 */
export default function HeartbeatMonitorPanel({ api, showToast, t }) {
  const [loading, setLoading] = useState(true);
  const [deployments, setDeployments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [ackTarget, setAckTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tenants, alertRes] = await Promise.all([
        api('/admin/tenants'),
        api('/admin/heartbeat-alerts', { params: { only_open: onlyOpen, limit: 200 } }),
      ]);
      const tlist = Array.isArray(tenants) ? tenants : (tenants?.items || []);
      // Deployments je Mandant parallel laden und zu einer flachen Liste aggregieren.
      const perTenant = await Promise.all(
        tlist.map(async (tn) => {
          try {
            const res = await api(`/admin/tenants/${tn.id}/onprem-licenses`);
            return (res?.deployments || []).map((d) => ({
              ...d, tenant_id: tn.id, tenant_name: tn.name,
            }));
          } catch {
            return [];
          }
        })
      );
      setDeployments(perTenant.flat());
      setAlerts(alertRes?.alerts || []);
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setLoading(false);
    }
  }, [api, onlyOpen, showToast, t]);

  useEffect(() => { load(); }, [load]);

  const doAck = async () => {
    if (!ackTarget) return;
    try {
      await api(`/admin/heartbeat-alerts/${ackTarget.id}/ack`, { method: 'POST' });
      showToast(t('license.monitor.ack_done', 'Alarm quittiert'), 'success');
      setAckTarget(null);
      await load();
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
      setAckTarget(null);
    }
  };

  // Zaehlungen fuer die Kopfzeile.
  const counts = deployments.reduce((acc, d) => {
    const lk = lifecycleStatus(d).key;
    const hk = heartbeatStatus(d).key;
    acc[lk] = (acc[lk] || 0) + 1;
    if (hk === 'silent') acc.silent = (acc.silent || 0) + 1;
    return acc;
  }, {});

  const depById = {};
  deployments.forEach((d) => { depById[d.deployment_id] = d; });

  const summaryTiles = [
    { key: 'active',  tone: 'ok',      label: t('license.onprem.lifecycle.active', 'aktiv') },
    { key: 'warn',    tone: 'warning', label: t('license.onprem.lifecycle.warn', 'Warnung') },
    { key: 'grace',   tone: 'warning', label: t('license.onprem.lifecycle.grace', 'Kulanz') },
    { key: 'expired', tone: 'danger',  label: t('license.onprem.lifecycle.expired', 'abgelaufen') },
    { key: 'revoked', tone: 'danger',  label: t('license.onprem.lifecycle.revoked', 'widerrufen') },
    { key: 'silent',  tone: 'danger',  label: t('license.monitor.silent', 'Funkstille') },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{t('license.monitor.title', 'Heartbeat-Ueberwachung')}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          {t('license.monitor.policy', 'Policy: Warnung {warn} Tage vor Ablauf, danach {grace} Tage Kulanz (read-only)')
            .replace('{warn}', String(WARN_DAYS)).replace('{grace}', String(GRACE_DAYS))}
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
          {t('license.monitor.refresh', 'Aktualisieren')}
        </button>
      </div>

      {/* Zusammenfassung */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 22 }}>
        {summaryTiles.map((tile) => (
          <div
            key={tile.key}
            style={{
              border: '1px solid var(--border)', borderRadius: 10, padding: '10px 16px',
              background: 'var(--bg-surface)', minWidth: 96,
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 800 }}>{counts[tile.key] || 0}</div>
            <div style={{ marginTop: 4 }}><LicenseBadge tone={tile.tone}>{tile.label}</LicenseBadge></div>
          </div>
        ))}
      </div>

      {/* Heartbeat-Alarme */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{t('license.monitor.alerts_title', 'Heartbeat-Alarme')}</div>
        <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />
          {t('license.monitor.only_open', 'Nur offene')}
        </label>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('common.loading', 'Lade...')}</div>
      ) : alerts.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', marginBottom: 24 }}>
          {t('license.monitor.no_alerts', 'Keine Alarme.')}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: 'var(--bg-surface)' }}>
                {['severity', 'type', 'deployment', 'detail', 'created', 'actions'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
                    {t(`license.monitor.col.${h}`, h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => {
                const dep = depById[a.deployment_id];
                return (
                  <tr key={a.id}>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
                      <LicenseBadge tone={a.severity === 'critical' ? 'danger' : 'warning'}>{a.severity}</LicenseBadge>
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', fontFamily: 'monospace' }}>{a.alert_type}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
                      {dep ? `${dep.tenant_name} / ${dep.host || dep.tool_id}` : (a.deployment_id || '-')}
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', maxWidth: 260, wordBreak: 'break-word' }}>{a.detail || '-'}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{a.created_at ? new Date(a.created_at).toLocaleString() : '-'}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
                      {a.acknowledged_at ? (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {t('license.monitor.acked_by', 'quittiert von')} {a.acknowledged_by || '-'}
                        </span>
                      ) : (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAckTarget(a)}>
                          {t('license.monitor.ack', 'Quittieren')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Aggregierte Deployments */}
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
        {t('license.monitor.deployments_title', 'Alle ONPREM-Deployments')}
      </div>
      {loading ? null : deployments.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
          {t('license.monitor.no_deployments', 'Keine Deployments.')}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: 'var(--bg-surface)' }}>
                {['tenant', 'host', 'tool', 'env', 'valid_until', 'lifecycle', 'heartbeat'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
                    {t(`license.monitor.dcol.${h}`, h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deployments.map((d) => {
                const life = lifecycleStatus(d);
                const hb = heartbeatStatus(d);
                return (
                  <tr key={d.deployment_id}>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{d.tenant_name}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{d.host || '-'}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{d.tool_id}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{d.environment}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{d.valid_until || '-'}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
                      <LicenseBadge tone={life.tone}>{t(`license.onprem.lifecycle.${life.key}`, life.key)}</LicenseBadge>
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
                      <LicenseBadge tone={hb.tone}>{t(`license.onprem.heartbeat.${hb.key}`, hb.key)}</LicenseBadge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {ackTarget && (
        <ConfirmDialog
          title={t('license.monitor.ack_title', 'Alarm quittieren')}
          message={t('license.monitor.ack_msg', 'Alarm wirklich quittieren? Dieselbe Bedingung kann danach erneut alarmieren.')}
          confirmLabel={t('license.monitor.ack', 'Quittieren')}
          cancelLabel={t('common.cancel', 'Abbrechen')}
          onConfirm={doAck}
          onCancel={() => setAckTarget(null)}
        />
      )}
    </div>
  );
}
